import {absoluteUrlToInternalResource} from '#core/utils/url-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {PALETTE_IDS} from '#core/application/modules/ids.js';

const COLOR = {
    SCRIM_LIGHT: 'rgba(0, 0, 0, 0.6)',
    SCRIM_DARK: 'rgba(0, 0, 0, 0.8)',

    // Colors from brandbook
    BRAND_BLACK: '#1a181b',
    BRAND_WHITE: '#f8f8f0',
    BRAND_VIOLET1: '#6a46f3',
    BRAND_VIOLET2: '#c325b4',
    BRAND_BLUE1: '#10c5de',
    BRAND_DARK_GRAY: '#414149',
    BRAND_LIGHT_GRAY: '#eaeaf0',
    BRAND_ORANGE: '#F6AE2D',

    BRAND_SURFACE: '#ffffff',
    BRAND_TEXT_BLACK: '#414149',
    BRAND_TEXT_DARK_GRAY: '#404149',
    BRAND_TEXT_NEUTRAL_GRAY: '#5A5A6B',
    BRAND_TEXT_GRAY: '#77788E',
    BRAND_TEXT_LIGHT_GRAY: '#adaebe',
    BRAND_TEXT_WHITE: '#f8f8fa',
    BRAND_WARNING_BG: '#fbf3d8',
    BRAND_SELECTION_BG: '#ECE8FE',
    BRAND_SELECTION_TEXT: '#6a46f3',

    // Custom colors
    CUSTOM_WHITE: '#fdfcfc',
    CUSTOM_GRAY: '#f8f8f8',
    CUSTOM_VIOLET: '#957bff',
    CUSTOM_TEXT_GRAY: '#818194',
    CUSTOM_DIVIDER: '#EAEAF0',

    GRADIENT: `linear-gradient(to right bottom, #6a46f3, #c325b4)`,
    GRADIENT_HORIZONTAL: `linear-gradient(to right, #6a46f3, #c325b4)`,
    GRADIENT2: `linear-gradient(to right bottom, #e5e5e5, #fdfcfc)`,
    GRADIENT_ACCENT: 'linear-gradient(90deg, #F5AC2F 0%, #C325B4 100%)',
};

const FONT = {
    FAMILY: 'NeusaNeuVT, sans-serif',

    WEIGHT_NORMAL: 400,
    WEIGHT_SEMI_BOLD: 500,
    WEIGHT_BOLD: 700,

    SIZE_XXSMALL: 12,
    SIZE_XSMALL: 12,
    SIZE_SMALL: 13,
    SIZE_MEDIUM: 14,
    SIZE_LARGE: 16,
    SIZE_XLARGE: 18,
};

export const THEME_LIGHT = {
    [PALETTE_IDS.BASE]: {
        FONT_FAMILY: 'NeusaNeuVT, sans-serif',
        VERTICAL_ALIGN_FIX: 4,

        FILL_PAGE: '#fcfcfe',
        FILL_HEADER: COLOR.BRAND_SURFACE,
        FILL_HERO: COLOR.BRAND_SURFACE,
        FILL_SHEET: COLOR.CUSTOM_GRAY,
        FILL_POPOVER: COLOR.BRAND_SURFACE,

        FILL_CONTENT_DEFAULT: COLOR.BRAND_SURFACE,
        FILL_CONTENT_PROMINENT: COLOR.BRAND_SURFACE,

        FILL_CARD: COLOR.BRAND_SURFACE,
        FILL_CARD_PROMINENT: COLOR.GRADIENT,

        FILL_CONTROL_BACKGROUND: '#123456',
        FILL_CONTROL_FILL: '#123456',
        FILL_CONTROL_OUTLINE: '#c3c3d4',
        FILL_CONTROL_FOREGROUND: '#123456',
        FILL_CONTROL_ACCENT: COLOR.BRAND_SELECTION_BG,
        FILL_CONTROL_ODDS: '#ECE8FE', //COLOR.BRAND_LIGHT_GRAY,

        FILL_ACCENT_PRIMARY: COLOR.BRAND_VIOLET1,
        FILL_ACCENT_SECONDARY: COLOR.BRAND_VIOLET2,
        FILL_ACCENT_FOCUS: COLOR.BRAND_SELECTION_BG,

        FILL_SELECTED_PRIMARY: COLOR.BRAND_SELECTION_BG,
        FILL_SELECTED_ODDS: COLOR.BRAND_BLUE1,

        DEFAULT_PRIMARY_TEXT: `${COLOR.BRAND_WHITE}|${COLOR.BRAND_TEXT_BLACK}`,
        DEFAULT_SECONDARY_TEXT: `#f8f8f0aa|#5A5A6B`,
        TEXT_DEFAULT_TERTIARY: '#5A5A6B',

        TEXT_PROMINENT_PRIMARY: COLOR.BRAND_TEXT_BLACK,
        TEXT_PROMINENT_SECONDARY: '#5A5A6B',
        TEXT_HERO_PRIMARY: COLOR.BRAND_TEXT_BLACK,
        TEXT_HERO_SECONDARY: '#5A5A6B',

        TEXT_ODDS_PRIMARY: COLOR.BRAND_VIOLET1,
        TEXT_ODDS_SECONDARY: '#5A5A6B',

        TEXT_ACCENT_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_ACCENT_SECONDARY: COLOR.BRAND_BLACK,
        TEXT_ACCENT_FILL: `${COLOR.BRAND_VIOLET1}|${COLOR.BRAND_WHITE}|${COLOR.BRAND_BLACK}`,

        TEXT_SELECTED_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_SELECTED_ODDS: COLOR.BRAND_WHITE,

        DEFAULT_GENERIC_ACCENT_COLOR: `${COLOR.BRAND_VIOLET1}|${COLOR.BRAND_VIOLET2}|${COLOR.BRAND_WHITE}|${COLOR.BRAND_BLACK}`,
        DEFAULT_DIVIDER: COLOR.CUSTOM_DIVIDER,

        FILL_SEMANTIC_ERROR: '#dd3054',
        FILL_SEMANTIC_SUCCESS: '#1bc37c',
        FILL_SEMANTIC_WARN: '#a5835f',

        DEFAULT_BUTTON_BORDER_RADIUS: 2,
        DEFAULT_DROPDOWN_BORDER_RADIUS: 2,
        DEFAULT_SHEET_BORDER_RADIUS: 2,
        DEFAULT_CARD_BORDER_RADIUS: 2,

        SHADOW_SHORT: '0 1px 10px 0 rgba(0, 0, 0, 0.15)',
        SHADOW_LONG: '0 2px 10px 0 rgba(0, 0, 0, 0.15)',
        DEFAULT_BOX_DECORATION_WITH_SHADOW: false,

        DEFAULT_TILE_COLOR_SCHEME: 'none',
    },
    // Components ============================
    [PALETTE_IDS.ODDS_BUTTON]: {
        ODDS_PRICE_TEXT: {
            color: COLOR.BRAND_BLUE1,
            fontWeight: FONT.WEIGHT_BOLD,
        },
        ODDS_SELECTED_INFO_TEXT: COLOR.BRAND_TEXT_WHITE,
        ODDS_INFO_TEXT: {
            color: '#77788e',
            fontSize: FONT.SIZE_XSMALL,
        },
        ODDS_HOVER_BG: '#dadae5',
    },
    [PALETTE_IDS.PIVOT]: {
        PIVOT_TEXT: {
            color: COLOR.BRAND_DARK_GRAY,
        },
        PIVOT_SELECTED_TEXT: {
            color: COLOR.BRAND_VIOLET1,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
        },
        PIVOT_ICON_COLOR: COLOR.BRAND_VIOLET1,
        PIVOT_INDICATOR_HEIGHT: 2,
        PIVOT_INDICATOR_BOTTOM: 2,
    },
    [PALETTE_IDS.MINI_SCOREBOARD]: {
        MINI_SCOREBOARD_SCORE_COUNT_TEXT: COLOR.BRAND_TEXT_BLACK,
    },
    [PALETTE_IDS.INPUT]: {
        INPUT_BG: COLOR.BRAND_LIGHT_GRAY,
        INPUT_BORDER_COLOR: COLOR.CUSTOM_DIVIDER,
        INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        INPUT_BORDER_RADIUS: 2,
        INPUT_TEXT: '#5E5E71',
    },
    [PALETTE_IDS.STAKE_INPUT]: {
        STAKE_INPUT_BG: 'transparent',
        STAKE_INPUT_BORDER_COLOR: '#D9D9D9', //'#404149',
        STAKE_INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        STAKE_INPUT_BORDER_RADIUS: 2,
        STAKE_INPUT_TEXT: COLOR.BRAND_TEXT_DARK_GRAY, //'#5E5E71',
    },
    [PALETTE_IDS.PRIMARY_BUTTON]: {
        PRIMARY_BUTTON_DISABLED_BG: '#DADAE5',
    },
    [PALETTE_IDS.PRIMARY_LINK]: {
        PRIMARY_LINK_BG: COLOR.BRAND_LIGHT_GRAY,
        PRIMARY_LINK_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        PRIMARY_LINK_ICON_COLOR: COLOR.BRAND_VIOLET1,
        PRIMARY_LINK_BORDER_RADIUS: 2,
        PRIMARY_LINK_TEXT: {
            color: COLOR.BRAND_TEXT_BLACK,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 16,
        },
    },
    [PALETTE_IDS.TOGGLE_BUTTON]: {
        TOGGLE_BUTTON_BG: COLOR.BRAND_LIGHT_GRAY,
        TOGGLE_BUTTON_SELECTED_BG: COLOR.BRAND_SELECTION_BG,
        TOGGLE_BUTTON_SELECTED_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        TOGGLE_BUTTON_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        TOGGLE_BUTTON_SELECTED_TEXT: {
            color: COLOR.BRAND_VIOLET1,
            fontWeight: FONT.WEIGHT_BOLD,
        },
    },
    [PALETTE_IDS.SWITCH]: {
        SWITCH_CONTAINER_BG: COLOR.BRAND_LIGHT_GRAY,
        SWITCH_CONTAINER_BORDER_COLOR: COLOR.BRAND_LIGHT_GRAY,
        SWITCH_BORDER_RADIUS: 24,
        SWITCH_BUTTON_BG: COLOR.BRAND_VIOLET1,
        //
    },
    [PALETTE_IDS.LIST_ITEM]: {
        LIST_ITEM_SELECTED_BG: COLOR.BRAND_SELECTION_BG,
        LIST_ITEM_SELECTED_TEXT: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.DROPDOWN_MENU]: {
        DROPDOWN_MENU_ITEM_SELECTED_TEXT: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.PRIMARY_DROPDOWN]: {
        PRIMARY_DROPDOWN_BG: COLOR.BRAND_LIGHT_GRAY,
    },
    [PALETTE_IDS.INFO_NOTIFICATION]: {
        INFO_BG: COLOR.BRAND_SELECTION_BG,
        INFO_BORDER: {
            BORDER_COLOR: COLOR.BRAND_SELECTION_BG,
        },
        INFO_TEXT: COLOR.BRAND_SELECTION_TEXT,
    },
    [PALETTE_IDS.SUCCESS_NOTIFICATION]: {
        SUCCESS_BG: '#8dc572',
        SUCCESS_BORDER: {
            BORDER_COLOR: '#8dc572',
        },
        SUCCESS_ICON_COLOR: COLOR.BRAND_DARK_GRAY,
    },
    [PALETTE_IDS.WARNING_NOTIFICATION]: {
        WARNING_BG: '#f0ad4e',
        WARNING_BORDER: {
            BORDER_COLOR: '#f0ad4e',
        },
    },
    [PALETTE_IDS.ERROR_NOTIFICATION]: {
        ERROR_BG: '#be6464',
        ERROR_BORDER: {
            BORDER_COLOR: '#be6464',
        },
    },
    [PALETTE_IDS.NEUTRAL_VENUE_INDICATOR]: {
        NVI_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.STREAMING_INDICATOR]: {
        INDICATOR_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.BONUS_INDICATOR]: {
        BONUS_INDICATOR_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        BONUS_INDICATOR_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.ODDS_BOOST_BANNER]: {
        ODDS_BOOSTS_CONTAINER_BORDER_RADIUS: 0,
        ODDS_BOOSTS_ACCENT_COLOR: COLOR.BRAND_VIOLET1,
        ODDS_BOOSTS_COMMON_COLOR: COLOR.BRAND_VIOLET1,
        ODDS_BOOSTS_ACTION_BUTTON_TEXT: {
            color: COLOR.BRAND_VIOLET1,
            textTransform: 'uppercase',
        },
    },

    // Betslip and Betting Center ============================

    [PALETTE_IDS.BETTING_CENTER]: {
        BETTING_CENTER_CONTENT_CONTAINER_BG: COLOR.BRAND_SURFACE,
        BETTING_CENTER_TAB_BUTTON_BG: '#ECE8FE',
        BETTING_CENTER_TAB_BUTTON_TEXT: {
            textTransform: 'uppercase',
        },
    },
    [PALETTE_IDS.BETSLIP]: {
        BETSLIP_BET_HIGHLIGHT_INDICATOR_COLOR: '#dd3054',
        BETSLIP_PILL_BASE_BG: COLOR.BRAND_VIOLET1,
        BETSLIP_PILL_SUCCESS_BG: '#05cd79',
        BETSLIP_PILL_SUCCESS_ICON_COLOR: COLOR.BRAND_TEXT_WHITE,
        BETSLIP_PILL_OUTLINE: {
            BORDER_COLOR: COLOR.BRAND_VIOLET1,
            BORDER_WIDTH: 2,
        },

        BETSLIP_BEST_ODDS_GUARANTEED_ICON_COLOR: COLOR.BRAND_VIOLET1,
        BETSLIP_BET_TYPE_SELECTOR: {
            PIVOT_TEXT: {
                fontSize: FONT.SIZE_SMALL,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
            },
            PIVOT_SELECTED_TEXT: {},
        },

        BETSLIP_REMOVE_ALL_BUTTON: {
            TERTIARY_BUTTON_TEXT: COLOR.BRAND_TEXT_BLACK,
        },
        BETSLIP_NOTIFICATION_DEPOSIT_BUTTON: {
            SECONDARY_BUTTON_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        },
    },
    [PALETTE_IDS.QUICK_BETSLIP]: {
        QUICK_BETSLIP_BEST_ODDS_GUARANTEED_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.BET_BASE_INFO]: {
        BET_ODDS_TEXT: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.BET_BUILDER_SELECTION_ITEM]: {
        BB_BET_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.CHANGED_VALUE_INDICATOR]: {
        CVI_NEW_VALUE_BG: COLOR.BRAND_WARNING_BG,
        CVI_INDICATION_ICON_COLOR: COLOR.BRAND_ORANGE,
    },
    [PALETTE_IDS.NUMERIC_KEYPAD]: {
        KEYPAD_INCREASE_BUTTON_TEXT: {
            color: COLOR.BRAND_BLACK,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 15,
        },
        KEYPAD_BUTTON: {
            TOGGLE_BUTTON_BG: COLOR.BRAND_SURFACE,
            TOGGLE_BUTTON_BORDER_COLOR: COLOR.BRAND_LIGHT_GRAY,
            TOGGLE_BUTTON_BORDER_RADIUS: 4,
            TOGGLE_BUTTON_TEXT: {
                color: COLOR.BRAND_BLACK,
                fontSize: 15,
            },
        },
        KEYPAD_COMPACT_BUTTON: {
            TOGGLE_BUTTON_BG: COLOR.BRAND_SURFACE,
            TOGGLE_BUTTON_BORDER_COLOR: COLOR.BRAND_LIGHT_GRAY,
            TOGGLE_BUTTON_BORDER_RADIUS: 4,
            TOGGLE_BUTTON_TEXT: {
                color: COLOR.BRAND_BLACK,
                fontSize: 15,
            },
        },
    },

    [PALETTE_IDS.CARDS_AND_PROMOTIONS]: {
        CARD_CONTAINER_BG: COLOR.BRAND_SELECTION_BG,
        CARD_ODDS_BUTTON: {
            ODDS_BG: COLOR.BRAND_SURFACE,
            ODDS_PRICE_TEXT: {
                color: COLOR.BRAND_BLUE1,
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
            },
            ODDS_INFO_TEXT: {
                textTransform: 'none',
                fontWeight: FONT.WEIGHT_NORMAL,
                color: COLOR.BRAND_TEXT_BLACK,
            },
        },
        CARD_WATERMARK_ICON_HIDDEN: true,
    },
    [PALETTE_IDS.PREBUILT_CARD_DETAILS]: {
        ODDS_BUTTON: {
            ODDS_BG: COLOR.BRAND_SURFACE,
            ODDS_PRICE_TEXT: {
                fontSize: FONT.SIZE_LARGE,
            },
            ODDS_SELECTED_PRICE_TEXT: {
                fontSize: FONT.SIZE_LARGE,
            },
        },
        ACCA_SELECTION_MARKET_TEXT: {
            fontWeight: FONT.WEIGHT_NORMAL,
        },
    },
    [PALETTE_IDS.PRE_BUILT_CARDS]: {
        PREBUILT_CARD_CONTAINER_BG: COLOR.BRAND_SELECTION_BG,
    },
    [PALETTE_IDS.BANNER_CARDS]: {
        CARD_BANNER_DETAILS_DARK: {
            HEADER_TEXT: COLOR.BRAND_WHITE,
            MESSAGE_TEXT: COLOR.BRAND_LIGHT_GRAY,
        },
    },
    [PALETTE_IDS.PRICE_BOOST_BANNER]: {
        PRICE_BOOST_BANNER_CONTAINER_BG: COLOR.GRADIENT_ACCENT,
        PRICE_BOOST_BANNER_BOOST_LABEL: {
            PRICE_BOOST_LABEL_TEXT: {
                color: '#fff',
                textTransform: 'uppercase',
            },
            PRICE_BOOST_LABEL_ICON_COLOR: '#fff',
        },
        PRICE_BOOST_BANNER_BOOST_ITEM: {
            PBI_PREVIOUS_PRICE_HEADER_TEXT: {
                color: '#fff',
                fontWeight: FONT.WEIGHT_NORMAL,
                fontSize: FONT.SIZE_XSMALL,
                textTransform: 'uppercase',
            },
            PBI_PREVIOUS_PRICE_TEXT: {
                color: '#fff',
                fontWeight: FONT.WEIGHT_NORMAL,
                fontSize: FONT.SIZE_XSMALL,
                textTransform: 'uppercase',
            },
            PBI_PREVIOUS_PRICE_INDICATION_ICON_COLOR: '#fff',
            PBI_ODDS_BUTTON: {
                ODDS_BG: COLOR.BRAND_SURFACE,
                ODDS_PRICE_TEXT: {
                    color: COLOR.BRAND_VIOLET1,
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_LARGE,
                },
                ODDS_SELECTED_PRICE_TEXT: {
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_LARGE,
                },
            },
        },
    },
    [PALETTE_IDS.PRICE_BOOST_LABEL]: {
        PRICE_BOOST_LABEL_TEXT: {
            fontStyle: 'normal',
        },
    },
    [PALETTE_IDS.PRICE_BOOST_ITEM]: {
        PBI_PREVIOUS_PRICE_INDICATION_ICON_COLOR: COLOR.BRAND_ORANGE,
    },
    [PALETTE_IDS.SEARCH_RESULT]: {
        SEARCH_RECENT_ITEM_BG: COLOR.BRAND_LIGHT_GRAY,
    },
    [PALETTE_IDS.HOME_PAGE_CLASSIC]: {
        HOME_PAGE_TOP_SPORTS_ROW_ICON_COLOR: COLOR.BRAND_VIOLET1,
        HOME_PAGE_TOP_SPORTS_SPORT_TEXT: {fontWeight: FONT.WEIGHT_BOLD},
        HOME_PAGE_TOP_SPORTS_CONTAINER_BG: 'transparent',
        HOME_PAGE_PROMOTION_QUICK_LINKS: {
            PROMOTION_QUICK_LINK: {
                PRIMARY_LINK_BG: 'transparent',
                PRIMARY_LINK_BORDER_COLOR: COLOR.BRAND_VIOLET1,
                PRIMARY_LINK_BORDER_RADIUS: 2,
                PRIMARY_LINK_ICON_COLOR: 'transparent',
                PRIMARY_LINK_TEXT: {
                    color: COLOR.BRAND_TEXT_BLACK,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    fontSize: 14,
                },
            },
        },
    },
    [PALETTE_IDS.EVENT_LIST]: {
        EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_TEXT: COLOR.BRAND_VIOLET1,
        EVENT_LIST_GROUP_MARKET_TEXT: {
            color: COLOR.BRAND_TEXT_GRAY,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 9,
            textTransform: 'uppercase',
        },
        EVENT_LIST_ITEM_SEPARATOR_COLOR: COLOR.BRAND_VIOLET1,
        EVENT_LIST_ALL_EVENTS_LINK: {
            SECONDARY_LINK_TEXT: {
                color: COLOR.BRAND_VIOLET1,
                textDecoration: 'underline',
            },
        },
        EVENT_LIST_GAME_LINE_ITEM_DETAILS: {
            EVENT_DETAILS_PARTICIPANTS: {
                PARTICIPANTS_TEXT: {
                    lineHeight: 1.29,
                },
                PITCHER_TEXT: {
                    lineHeight: 1.17,
                },
            },
        },
    },
    [PALETTE_IDS.EVENT_LIST_HEADER]: {
        EVENT_LIST_HEADER_MARKETS_TEXT: {
            color: COLOR.BRAND_TEXT_GRAY,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
        },
    },
    [PALETTE_IDS.EVENT_CARDS]: {
        CARD_EVENT_DETAILS_LIGHT: {
            ODDS_BUTTON: {
                ODDS_INFO_TEXT: {
                    color: COLOR.BRAND_TEXT_GRAY,
                },
            },
        },
    },
    [PALETTE_IDS.EVENT_CARD_DETAILS]: {
        EVENT_NAME_TEXT: {
            lineHeight: '16px',
        },
        TIMING_STATUS: {
            EVENT_TIMING_LIVE_TEXT: {
                lineHeight: '16px',
            },
            EVENT_TIMING_UPCOMING_TEXT: {
                lineHeight: '16px',
            },
        },
        ODDS_BUTTON: {
            ODDS_INFO_TEXT: {
                textTransform: 'none',
            },
        },
    },
    [PALETTE_IDS.MARKETS]: {
        MARKETS_GAME_LINE_PARTICIPANTS: {
            PARTICIPANTS_TEXT: {
                lineHeight: 1.29,
            },
            PITCHER_TEXT: {
                lineHeight: 1.17,
            },
        },
        MARKETS_SELECTION_HEADER_LABEL_TEXT: {
            color: COLOR.BRAND_TEXT_GRAY,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 9,
            textTransform: 'uppercase',
        },
        MARKETS_PRICE_BOOST_LABEL: {
            PRICE_BOOST_LABEL_ICON_COLOR: COLOR.BRAND_TEXT_BLACK,
        },
        MARKETS_PRICE_BOOST_ITEM: {
            PBI_ODDS_BUTTON: {
                ODDS_BG: COLOR.GRADIENT_ACCENT,
                ODDS_HOVER_BG: 'linear-gradient(90deg, #f2ae3a 0%, #d437c5 100%)',
                ODDS_SELECTED_BG: 'linear-gradient(90deg, #cc8b1d 0%, #99178d 100%)',
                ODDS_INFO_TEXT: '#fff',
                ODDS_PRICE_TEXT: '#fff',
            },
        },
    },
    [PALETTE_IDS.COUPONS]: {
        COUPONS_TITLE_TEXT: {
            fontSize: 16,
            fontWeight: FONT.WEIGHT_BOLD,
            color: COLOR.BRAND_VIOLET1,
            textTransform: 'capitalize',
        },
        TOP_SPORTS_SPORT_SELECTOR: {
            TOGGLE_BUTTON_ICON_COLOR: COLOR.BRAND_VIOLET1,
            TOGGLE_BUTTON_BG: COLOR.BRAND_SURFACE,
        },
        TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG: COLOR.BRAND_SURFACE,
        COUPONS_BORDER: {
            BORDER_RADIUS: 2,
            BORDER_SHOW_SHADOW: false,
        },
        PRICE_BOOST_COUPON_ITEM: {
            PBI_ODDS_BUTTON: {
                ODDS_BG: COLOR.GRADIENT_ACCENT,
                ODDS_HOVER_BG: 'linear-gradient(90deg, #f2ae3a 0%, #d437c5 100%)',
                ODDS_SELECTED_BG: 'linear-gradient(90deg, #cc8b1d 0%, #99178d 100%)',
                ODDS_INFO_TEXT: '#fff',
                ODDS_PRICE_TEXT: '#fff',
            },
        },
    },
    [PALETTE_IDS.SPORT_PAGE]: {
        LEAGUES_SECTION_HEADER_TEXT: {
            fontSize: 16,
            fontWeight: FONT.WEIGHT_BOLD,
            color: COLOR.BRAND_TEXT_BLACK,
            textTransform: 'none',
        },
        LEAGUES_CONTAINER_BORDER: {
            BORDER_SHOW_SHADOW: false,
        },
        COMPETITIONS_PROMOTION_QUICK_LINKS: {
            PROMOTION_QUICK_LINK: {
                PRIMARY_LINK_TEXT: {fontSize: FONT.SIZE_MEDIUM},
            },
        },
    },
    [PALETTE_IDS.INPLAY_PAGE]: {
        INPLAY_SPORTS_SELECTOR: {
            TOGGLE_BUTTON_ICON_COLOR: COLOR.BRAND_VIOLET1,
            TOGGLE_BUTTON_BG: COLOR.BRAND_SURFACE,
        },
    },
    [PALETTE_IDS.VIRTUALS]: {
        DESKTOP_SPORT_MENU: {
            LIST_ITEM_ICON_COLOR: COLOR.BRAND_VIOLET1,
        },
    },
    [PALETTE_IDS.BONUSES]: {
        PROMOTION_CODE_INPUT: {
            INPUT_BG: COLOR.BRAND_SURFACE,
            INPUT_BORDER_COLOR: COLOR.CUSTOM_VIOLET,
        },
    },
    // Interface Elements ============================
    [PALETTE_IDS.MENUS_LINKS_LISTS]: {
        LHM_CONTAINER_BG: 'transparent',
        LHM_HEADER_BG: 'transparent',
        LHM_HEADER_TEXT: {
            color: COLOR.BRAND_VIOLET1,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 16,
        },
    },
    [PALETTE_IDS.QUICK_NAVIGATIONS]: {
        QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR: {
            TOGGLE_BUTTON_ICON_COLOR: COLOR.BRAND_VIOLET1,
        },
    },
    [PALETTE_IDS.MOBILE_NAVBAR]: {
        CONTAINER_BG: COLOR.BRAND_VIOLET1,
        CONTAINER_BORDER_RADIUS: 10,
        LABEL_TEXT: '#fff',
    },
    [PALETTE_IDS.FAVORITE_ICON]: {
        FAVORITE_ICON_COLOR: COLOR.BRAND_VIOLET1,
        FAVORITE_ICON_TOGGLED_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.AZ_MENU]: {
        DESKTOP_AZ_SPORTS_LIST: {
            LIST_ITEM_ICON_COLOR: COLOR.BRAND_VIOLET1,
        },
        DESKTOP_AZ_EXPAND_BUTTON: {
            TERTIARY_BUTTON_TEXT: {
                color: COLOR.BRAND_VIOLET2,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
                textDecoration: 'underline',
            },
        },
        MOBILE_AZ_SECTION_HEADER_TEXT: COLOR.BRAND_VIOLET1,
        MOBILE_AZ_SPORTS_LIST: {
            LIST_ITEM_ICON_COLOR: COLOR.BRAND_VIOLET1,
        },
        MOBILE_AZ_ALL_SPORTS_BUTTON: {
            TERTIARY_BUTTON_TEXT: {
                color: COLOR.BRAND_VIOLET2,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
            },
        },
    },
    [PALETTE_IDS.PROMOTION_QUICK_LINKS]: {
        PROMOTION_QUICK_LINK: {
            PRIMARY_LINK_ICON_COLOR: COLOR.BRAND_TEXT_BLACK,
            PRIMARY_LINK_TEXT: {
                _mobile_fontWeight: FONT.WEIGHT_BOLD,
            },
        },
    },
    [PALETTE_IDS.SCOREBOARD]: {
        SCOREBOARD_ACTIVE_SCORE_TEXT: COLOR.BRAND_TEXT_WHITE,
    },
    [PALETTE_IDS.PROMOTIONS]: {
        BANNER_CONTAINER_BG: 'transparent',
    },
    [PALETTE_IDS.BAD_ROUTE_PAGE]: {
        BAD_ROUTE_PAGE_BG: COLOR.BRAND_LIGHT_GRAY,
        BAD_ROUTE_PAGE_INFO_CONTAINER_BG: 'rgba(255, 255, 255, 0.6)',
        BAD_ROUTE_PAGE_TITLE_TEXT: {
            color: COLOR.BRAND_VIOLET1,
            fontSize: '32px',
            fontWeight: FONT.WEIGHT_BOLD,
        },
        BAD_ROUTE_PAGE_INFO_TEXT: {
            fontSize: FONT.SIZE_MEDIUM,
        },
        BAD_ROUTE_PAGE_HOME_BUTTON: {
            PRIMARY_BUTTON_BG: 'transparent',
            PRIMARY_BUTTON_TEXT: {
                color: COLOR.BRAND_VIOLET1,
                fontSize: FONT.SIZE_LARGE,
                fontWeight: FONT.WEIGHT_BOLD,
                textDecoration: 'underline',
            },
        },
    },
    statscore: {
        // Config for statscore DBX feature
        livematchProConfigurationId: '6994668d2448b147ea0f058c', // light theme statscore widget
        prematchProConfigurationId: '69946699e230782e960c65ff',
        languagesMapping: {
            'es-MX': 'es',
        },
    },
    mojitoConfiguration: {
        services: {
            configurations: {
                // HomePageClassic: {
                //     layoutParts: {
                //         Search: {
                //             position: 4,
                //         },
                //     },
                // },
                BaseEventCard: {
                    container: {
                        style: {
                            backgroundColor: COLOR.BRAND_SELECTION_BG,
                            backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-guitar-watermark-light.png')})`,
                            backgroundPosition: 'right bottom',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                        },
                    },
                },
                // MatchEventCardClassic: {
                //     eventCard: {
                //         container: {
                //             style: {
                //                 backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-events-watermark-light.png')})`,
                //                 backgroundPosition: 'right top',
                //                 backgroundSize: '100%',
                //             },
                //         },
                //     },
                // },
                SelectionCardClassic: {
                    eventCardDetails: {
                        genericEventCard: {
                            container: {
                                style: {
                                    backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-events-watermark-light.png')})`,
                                    backgroundPosition: 'right top',
                                    backgroundSize: '100%',
                                },
                            },
                            clickableArea: {
                                style: {
                                    base: {
                                        paddingBottom: 5,
                                    },
                                },
                            },
                        },
                        boostedEventCard: {
                            container: {
                                style: {
                                    backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-odds-boost-watermark-light.png')})`,
                                    backgroundSize: '35%',
                                    backgroundPosition: 'right top',
                                },
                            },
                            clickableArea: {
                                style: {
                                    base: {
                                        paddingBottom: 5,
                                    },
                                },
                            },
                        },
                    },
                    headerContainer: {
                        style: {
                            height: 'auto',
                        },
                    },
                    selectionInfo: {
                        style: {
                            justifyContent: 'center',
                        },
                    },
                },
                PrebuiltBetClassic: {
                    genericEventCard: {
                        container: {
                            style: {
                                backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-bet-builder-watermark-light.png')})`,
                                backgroundPosition: '50% bottom',
                                backgroundSize: 'cover',
                            },
                        },
                    },
                },
                SportPage: {
                    pageHeader: {
                        backgroundImage: {
                            src: 'sport-page/generic_light.png',
                            _mobile_src: 'sport-page/mobile/generic_light.png',
                        },
                    },
                    sportBackgroundImages: [],
                    _mobile_sportBackgroundImages: [],
                },
                InPlayPage: {
                    pageHeader: {
                        headerContainer: {
                            style: {
                                backgroundColor: 'transparent',
                            },
                        },
                        label: {
                            style: {
                                color: COLOR.BRAND_VIOLET1,
                                fontSize: FONT.SIZE_LARGE,
                                fontWeight: FONT.WEIGHT_BOLD,
                                paddingLeft: 8,
                            },
                        },
                        icon: {
                            src: null,
                        },
                    },
                    pageTitle: {
                        text: {
                            style: {
                                color: COLOR.BRAND_VIOLET1,
                                fontSize: FONT.SIZE_LARGE,
                                fontWeight: FONT.WEIGHT_BOLD,
                            },
                        },
                        iconButtonContainer: {
                            style: {
                                display: 'none',
                            },
                        },
                        icon: {
                            buttonIcon: {
                                src: null,
                            },
                        },
                    },
                },
                InPlayMenu: {
                    rootContainer: {
                        style: {
                            padding: '0 0 8px 0',
                            _mobile_padding: '0 8px',
                        },
                    },
                },
                EventList: {
                    eventItem: {
                        gameLineMarketContent: {
                            market: {
                                selectionButton: {
                                    view: {
                                        priceText: {
                                            inactive: {
                                                color: COLOR.BRAND_VIOLET1,
                                            },
                                            active: {
                                                color: COLOR.BRAND_VIOLET1,
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        genericMarketContent: {
                            market: {
                                selectionButton: {
                                    view: {
                                        priceText: {
                                            inactive: {
                                                color: COLOR.BRAND_VIOLET1,
                                            },
                                            active: {
                                                color: COLOR.BRAND_VIOLET1,
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                Market: {
                    selectionButton: {
                        view: {
                            priceText: {
                                inactive: {
                                    color: COLOR.BRAND_VIOLET1,
                                },
                                active: {
                                    color: COLOR.BRAND_VIOLET1,
                                },
                            },
                        },
                    },
                },
                CorrectScoreMarketContent: {
                    selectionButton: {
                        view: {
                            priceText: {
                                inactive: {
                                    color: COLOR.BRAND_VIOLET1,
                                },
                                active: {
                                    color: COLOR.BRAND_VIOLET1,
                                },
                            },
                        },
                    },
                },
                PriceBoostPageView: {
                    pageTitle: {
                        container: {
                            style: {
                                ...resolveBackground(COLOR.GRADIENT_ACCENT),
                            },
                        },
                        icon: {
                            buttonIcon: {
                                style: {
                                    fill: '#fff',
                                },
                            },
                        },
                        text: {
                            style: {
                                color: '#fff',
                            },
                        },
                    },
                },
                PriceBoostAggregatedMarket: {
                    titleContainer: {
                        style: {
                            ...resolveBackground(COLOR.GRADIENT_ACCENT),
                        },
                    },
                    titleIcon: {
                        style: {
                            fill: '#fff',
                        },
                    },
                    titleText: {
                        style: {
                            padding: 0,
                            fontWeight: FONT.WEIGHT_NORMAL,
                            color: '#fff',
                        },
                    },
                },
            },
        },
    },
};
